import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, XIcon, ClockIcon, FileTextIcon, UsersIcon, CreditCardIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface SearchResult {
  id: string;
  type: 'transaction' | 'customer' | 'invoice' | 'dispute';
  title: string;
  subtitle: string;
  link: string;
}
export function UniversalSearchBar() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Mock search results
  const mockResults: SearchResult[] = [{
    id: '1',
    type: 'transaction',
    title: 'TXN-2024-0100',
    subtitle: 'THB 100.01 • 1 Dec 2024',
    link: '/sales/1'
  }, {
    id: '2',
    type: 'customer',
    title: 'John Doe',
    subtitle: 'customer@example.com',
    link: '/customers/1'
  }, {
    id: '3',
    type: 'invoice',
    title: 'INV-2024-001',
    subtitle: 'THB 1,500.00 • Due 15 Dec 2024',
    link: '/invoices/1'
  }, {
    id: '4',
    type: 'dispute',
    title: 'DP-2024-0001',
    subtitle: 'Fraudulent • Requires response',
    link: '/disputes/1'
  }];
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (query.length > 0) {
      const filtered = mockResults.filter(result => result.title.toLowerCase().includes(query.toLowerCase()) || result.subtitle.toLowerCase().includes(query.toLowerCase()));
      setResults(filtered);
      setIsOpen(true);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  }, [query]);
  const handleResultClick = (link: string) => {
    navigate(link);
    setQuery('');
    setIsOpen(false);
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'transaction':
        return <CreditCardIcon size={16} className="text-blue-600" data-id="element-4766" />;
      case 'customer':
        return <UsersIcon size={16} className="text-green-600" data-id="element-4767" />;
      case 'invoice':
        return <FileTextIcon size={16} className="text-purple-600" data-id="element-4768" />;
      case 'dispute':
        return <ClockIcon size={16} className="text-red-600" data-id="element-4769" />;
      default:
        return <SearchIcon size={16} className="text-gray-600" data-id="element-4770" />;
    }
  };
  return <div ref={searchRef} className="relative w-full max-w-2xl" data-id="element-4771">
      <div className="relative" data-id="element-4772">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4773">
          <SearchIcon size={18} className="text-gray-400" data-id="element-4774" />
        </div>
        <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search transactions, customers, invoices..." className="block w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" data-id="element-4775" />
        {query && <button onClick={() => {
        setQuery('');
        setIsOpen(false);
      }} className="absolute inset-y-0 right-0 pr-3 flex items-center" data-id="element-4776">
            <XIcon size={18} className="text-gray-400 hover:text-gray-600" data-id="element-4777" />
          </button>}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && results.length > 0 && <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto" data-id="element-4778">
          <div className="p-2" data-id="element-4779">
            <div className="text-xs font-medium text-gray-500 px-3 py-2" data-id="element-4780">
              Search Results ({results.length})
            </div>
            {results.map(result => <button key={result.id} onClick={() => handleResultClick(result.link)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-gray-50 transition-colors" data-id="element-4781">
                <div className="flex-shrink-0" data-id="element-4782">{getTypeIcon(result.type)}</div>
                <div className="flex-1 text-left" data-id="element-4783">
                  <div className="text-sm font-medium text-gray-900" data-id="element-4784">
                    {result.title}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-4785">{result.subtitle}</div>
                </div>
                <div className="text-xs text-gray-400 capitalize" data-id="element-4786">
                  {result.type}
                </div>
              </button>)}
          </div>
        </div>}

      {/* No Results */}
      {isOpen && query.length > 0 && results.length === 0 && <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4" data-id="element-4787">
          <div className="text-center text-sm text-gray-500" data-id="element-4788">
            No results found for "{query}"
          </div>
        </div>}
    </div>;
}